import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PiScrollLight } from "react-icons/pi";
import {
  BullhornIcon,
  CalendarIcon,
  CaretLeftIcon,
  CaretRightIcon,
} from "../Icons/SiteIcons";
import { getActiveAdvertisements } from "../../api/advertisementService";
import { getActiveTenders } from "../../api/tenderService";
import { getActiveEvents } from "../../api/eventsService";

const MAX_ITEMS = 5;

const LoadingSkeleton = () => (
  <div className="announcement-items">
    {[...Array(4)].map((_, index) => (
      <div key={index} className="announcement-item skeleton-item">
        <div className="item-content">
          <div className="skeleton skeleton-title"></div>
          <div className="skeleton skeleton-date"></div>
        </div>
      </div>
    ))}
  </div>
);

const EmptyState = ({ message }) => (
  <div className="empty-state">
    <p>{message}</p>
  </div>
);

const formatDate = (dateString) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "long",
      year: "numeric"
    });
  } catch (error) {
    return "";
  }
};

const AnnouncementCard = ({
  title,
  items,
  basePath,
  loading,
  emptyMessage,
  icon,
  iconColor,
  cardClass = ""
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = MAX_ITEMS;
  const totalPages = Math.ceil((items?.length || 0) / itemsPerPage);

  const currentItems = items?.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  return (
    <div className={`announcement-card ${cardClass}`} style={{ "--icon-color": iconColor }}>
      <div className="card-header">
        <h3 className="card-title">{title}</h3>
        <div className="card-nav">
          <button
            className="nav-btn"
            onClick={handlePrevPage}
            disabled={currentPage === 0}
            aria-label="Önceki sayfa"
          >
            <CaretLeftIcon />
          </button>
          <button
            className="nav-btn"
            onClick={handleNextPage}
            disabled={currentPage === totalPages - 1}
            aria-label="Sonraki sayfa"
          >
            <CaretRightIcon />
          </button>
        </div>
      </div>

      <div className="card-body">
        {loading ? (
          <LoadingSkeleton />
        ) : !currentItems || currentItems.length === 0 ? (
          <EmptyState message={emptyMessage} />
        ) : (
          <div className="announcement-items">
            {currentItems.map((item) => (
              <Link
                key={item.guid || item.id}
                to={`${basePath}/${item.urlTag || item.slug || item.id}`}
                state={{ guid: item.guid }}
                className="announcement-item"
              >
                <span className="item-chip">
                  <span className="chip-icon" aria-hidden="true">
                    {icon}
                  </span>
                </span>

                <div className="item-content">
                  <h4 className="item-title">{item.header || item.title}</h4>
                  <span className="item-date">
                    {formatDate(item.date || item.newsDate || item.startDate)}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default function AnnouncementsSection() {
  const [ilanlar, setIlanlar] = useState([]);
  const [ihaleler, setIhaleler] = useState([]);
  const [etkinlikler, setEtkinlikler] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);

        const [ilanRes, ihaleRes, etkinlikRes] = await Promise.allSettled([
          getActiveAdvertisements(),
          getActiveTenders(),
          getActiveEvents()
        ]);

        if (!mounted) return;

        if (ilanRes.status === "fulfilled") {
          const ilanItems = ilanRes.value?.data?.items || ilanRes.value?.data || [];
          setIlanlar(Array.isArray(ilanItems) ? ilanItems : []);
        } else {
          setIlanlar([]);
        }

        if (ihaleRes.status === "fulfilled") {
          const ihaleItems = ihaleRes.value?.data?.items || ihaleRes.value?.data || [];
          setIhaleler(Array.isArray(ihaleItems) ? ihaleItems : []);
        } else {
          setIhaleler([]);
        }

        if (etkinlikRes.status === "fulfilled") {
          const etkinlikItems = etkinlikRes.value?.data?.items || etkinlikRes.value?.data || [];
          setEtkinlikler(Array.isArray(etkinlikItems) ? etkinlikItems : []);
        } else {
          setEtkinlikler([]);
        }
      } catch (err) {
        if (!mounted) return;
        setIlanlar([]);
        setIhaleler([]);
        setEtkinlikler([]);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="announcements-section">
      <div className="services-grid-title">
        <h2>İlanlar ve İhaleler</h2>
        <p>Güncel ilan ve ihaleleri buradan takip edebilirsiniz.</p>
      </div>
      <div className="container">
        <div className="announcements-grid">
          <AnnouncementCard
            title="İLANLAR"
            items={ilanlar}
            basePath="/ilanlar"
            loading={loading}
            emptyMessage="Kayıt bulunamadı"
            icon={<BullhornIcon />}
            iconColor="#f59e0b"
            cardClass="announcements-card"
          />

          <AnnouncementCard
            title="İHALELER"
            items={ihaleler}
            basePath="/ihaleler"
            loading={loading}
            emptyMessage="Kayıt bulunamadı"
            icon={<PiScrollLight />}
            iconColor="#f59e0b"
            cardClass="tenders-card"
          />

          <AnnouncementCard
            title="ETKİNLİKLER"
            items={etkinlikler}
            basePath="/etkinlikler"
            loading={loading}
            emptyMessage="Kayıt bulunamadı"
            icon={<CalendarIcon />}
            iconColor="#f59e0b"
            cardClass="event-card"
          />
        </div>
      </div>
    </div>
  );
}
